import {Button, closeCurrentDialog, openDialog, Separator} from 'platform/components';
import {HStack, Icon, Right, Show, Spinner, Text, VStack} from 'platform/foundation';

import {useEffect, useState} from 'react';

import {isNil} from 'ramda';
import {isEmptyArray, isNilOrEmpty} from 'ramda-adjunct';

import {useGetSimilarVehiclesV2Query, useLazyGetSimilarVehiclesV2Query} from '@dms/api/sales';
import {GetSimilarVehiclesApiResponse} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps, useDebouncedValue} from 'shared/web';

import {handleApiError} from '../../../utils/handleApiError';
import {ServiceVehicleForm} from '../../ServiceVehicleForm/ServiceVehicleForm';
import {ServiceVehicleFormMode} from '../../ServiceVehicleForm/types/ServiceVehicleFormMode';
import {ServiceVehicleFormVariant} from '../../ServiceVehicleForm/types/ServiceVehicleFormVariant';
import {MatchingVehicle} from './MatchingVehicle';

const DEFAULT_VEHICLE_COUNT = 4;

export interface MatchingVehiclesProps extends TestIdProps {
  vin: string | Nullish;
  registrationPlateContains: string | Nullish;
  onVehicleSelect?: (vehicleId: string) => void;
}

export function MatchingVehicles(props: MatchingVehiclesProps) {
  const [moreVehicles, setMoreVehicles] = useState<GetSimilarVehiclesApiResponse | undefined>(
    undefined
  );

  const debouncedVin = useDebouncedValue(props.vin, 700);
  const debouncedRegistrationPlateContains = useDebouncedValue(
    props.registrationPlateContains,
    700
  );

  const [getSimilarVehicles, {isLoading: isLoadingMore, isFetching: isFetchingMore}] =
    useLazyGetSimilarVehiclesV2Query();
  const {
    data: similarVehicles,
    isLoading,
    isFetching,
  } = useGetSimilarVehiclesV2Query(
    {
      size: DEFAULT_VEHICLE_COUNT,
      registrationPlateContains: debouncedRegistrationPlateContains ?? undefined,
      vinContains: debouncedVin ?? undefined,
    },
    {skip: !debouncedVin && !debouncedRegistrationPlateContains}
  );

  useEffect(() => {
    setMoreVehicles(undefined);
  }, [debouncedVin, debouncedRegistrationPlateContains]);

  const onLoadMoreClick = async () => {
    const moreVehiclesResponse = await getSimilarVehicles({
      offset: DEFAULT_VEHICLE_COUNT,
      registrationPlateContains: debouncedRegistrationPlateContains,
      vinContains: debouncedVin,
    })
      .unwrap()
      .catch(handleApiError);

    if (moreVehiclesResponse) {
      setMoreVehicles(moreVehiclesResponse.vehicles);
    }
  };

  const onCreateVehicle = () => {
    openDialog(
      <ServiceVehicleForm
        mode={ServiceVehicleFormMode.NEW}
        variant={ServiceVehicleFormVariant.SIMPLE}
        extendedDefaultFormData={{
          vin: props.vin,
          registrationPlate: props.registrationPlateContains,
        }}
        onSuccess={(newVehicleId) => {
          props.onVehicleSelect?.(newVehicleId);
          closeCurrentDialog();
        }}
        onDiscard={closeCurrentDialog}
        data-testid={suffixTestId('create-vehicle', props)}
      />,
      {title: i18n.t('entity.vehicle.labels.createVehicle'), withAdditionalFooter: true}
    );
  };

  const isEmpty = isEmptyArray(similarVehicles?.vehicles);
  const isLoadingVehicles = isFetching || isLoading;
  const isLoadingMoreVehicles = isLoadingMore || isFetchingMore;
  const isShowingMatchingVehicles = debouncedVin || debouncedRegistrationPlateContains;
  const isCreatePrimary = !isShowingMatchingVehicles || isNilOrEmpty(similarVehicles?.vehicles);

  return (
    <VStack spacing={4}>
      <Show when={isShowingMatchingVehicles}>
        <Show when={isLoadingVehicles || isEmpty}>
          <HStack spacing={2} align="center" justify="center" height={22}>
            <Show when={isLoadingVehicles}>
              <Spinner size="small" />
              <Text size="small" color="tertiary" data-testid={suffixTestId('searching', props)}>
                {i18n.t('entity.vehicle.labels.searching')}
              </Text>
            </Show>
            <Show when={isEmpty && !isLoadingVehicles}>
              <Icon value="notification/do_disturb" color="text.tertiary" size={4} />
              <Text size="small" color="tertiary" data-testid={suffixTestId('noVehicles', props)}>
                {i18n.t('entity.vehicle.labels.noMatchingVehicles')}
              </Text>
            </Show>
          </HStack>
        </Show>
        <Show when={!isLoadingVehicles && !isEmpty}>
          <Text size="xSmall" color="secondary">
            {`${i18n.t('entity.vehicle.labels.matchingVehicles')} (${similarVehicles?.totalCount ?? 0})`}
          </Text>
          <VStack>
            {similarVehicles?.vehicles.map((vehicle, index) => (
              <VStack key={vehicle.id}>
                <Show when={index > 0}>
                  <Separator />
                </Show>
                <MatchingVehicle
                  onSelect={props.onVehicleSelect}
                  vehicle={vehicle}
                  searchedVin={props.vin}
                  searchedRegistrationPlate={props.registrationPlateContains}
                  data-testid={suffixTestId(`vehicle-[${index}]`, props)}
                />
              </VStack>
            ))}
            {moreVehicles?.map((vehicle, index) => (
              <VStack key={vehicle.id}>
                <Separator />
                <MatchingVehicle
                  onSelect={props.onVehicleSelect}
                  vehicle={vehicle}
                  searchedVin={props.vin}
                  searchedRegistrationPlate={props.registrationPlateContains}
                  data-testid={suffixTestId(`vehicle-[${index + DEFAULT_VEHICLE_COUNT}]`, props)}
                />
              </VStack>
            ))}
          </VStack>
        </Show>
        <Show
          when={(similarVehicles?.totalCount ?? 0) > DEFAULT_VEHICLE_COUNT && isNil(moreVehicles)}
        >
          <Button
            variant="link"
            onClick={onLoadMoreClick}
            isLoading={isLoadingMoreVehicles}
            leftIcon="navigation/expand_more"
            title={i18n.t('entity.customer.actions.more')}
            data-testid={suffixTestId(`more`, props)}
          />
        </Show>
      </Show>
      <Right>
        <Button
          onClick={onCreateVehicle}
          variant={isCreatePrimary ? 'primary' : 'secondary'}
          title={i18n.t('entity.vehicle.actions.createNewVehicle')}
          data-testid={suffixTestId(`createNewVehicle`, props)}
        />
      </Right>
    </VStack>
  );
}
